import React, { useState } from 'react';
import type { GuestCheckoutFormData, GuestCheckoutFormErrors } from '@/features/cart/types';
import { createFormValidator, commonRules } from '@/lib/utils';
import { FormInput } from '@/features/user/components/signup/FormInput';
import { SubmitButton } from '@/features/user/components/signup/SubmitButton';

interface GuestCheckoutFormProps {
  onSubmit: (data: GuestCheckoutFormData) => Promise<void>;
  isSubmitting?: boolean;
}

export const GuestCheckoutForm: React.FC<GuestCheckoutFormProps> = ({ onSubmit, isSubmitting = false }) => {
  const [formData, setFormData] = useState<GuestCheckoutFormData>({
    email: '',
    firstName: '',
    lastName: ''
  });
  const [errors, setErrors] = useState<GuestCheckoutFormErrors>({});

  // Create form validator
  const validator = createFormValidator({
    email: commonRules.email('Please enter a valid email address'),
    firstName: commonRules.required('First name is required'),
    lastName: commonRules.required('Last name is required'),
  });

  const handleInputChange = (field: keyof GuestCheckoutFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors = validator.validateForm(formData);
    setErrors(newErrors);
    return validator.isFormValid(formData);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Guest checkout form error:', error);
      setErrors(prev => ({ 
        ...prev, 
        general: 'Something went wrong. Please try again.' 
      }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Email Field */}
      <FormInput
        label="Email Address"
        type="email"
        value={formData.email}
        onChange={(value: string) => handleInputChange('email', value)}
        error={errors.email}
        placeholder="john@example.com"
      />

      {/* First Name Field */}
      <FormInput
        label="First Name"
        type="text"
        value={formData.firstName}
        onChange={(value: string) => handleInputChange('firstName', value)}
        error={errors.firstName}
        placeholder="John"
      />

      {/* Last Name Field */}
      <FormInput
        label="Last Name"
        type="text"
        value={formData.lastName}
        onChange={(value: string) => handleInputChange('lastName', value)}
        error={errors.lastName}
        placeholder="Doe"
      />

      {/* General Error */}
      {errors.general && (
        <div className="text-red-400 text-sm text-center">
          {errors.general}
        </div>
      )}

      {/* Submit Button */}
      <SubmitButton 
        isSubmitting={isSubmitting} 
        text="Continue to Checkout" 
        loadingText="Processing..." 
      />
    </form>
  );
};

